"use client";

import { Skeleton } from "@nyntax/design-system/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@nyntax/design-system/components/ui/table";
import { cn } from "@nyntax/design-system/lib/utils";
import type { ReactNode } from "react";
import { useDataTable } from "./data-table-context";
import { DataTableHeader } from "./data-table-header";

const SKELETON_WIDTHS = ["w-3/4", "w-1/2", "w-2/3", "w-4/5", "w-3/5"];

interface DataTableSkeletonProps {
  className?: string;
  /** Number of skeleton rows to render (default: 5) */
  rows?: number;
}

/**
 * Renders a skeleton loading state for the data table.
 *
 * Compose as a sibling/alternative to `DataTableContent`:
 * ```tsx
 * <DataTable columns={columns} data={data}>
 *   {isLoading ? <DataTableSkeleton rows={10} /> : <DataTableContent />}
 *   <DataTableFooter>...</DataTableFooter>
 * </DataTable>
 * ```
 *
 * Supports per-column custom skeletons via `meta.skeleton` in column definitions:
 * ```tsx
 * const columns = [
 *   {
 *     accessorKey: "name",
 *     header: "Name",
 *     meta: {
 *       skeleton: (
 *         <div className="flex items-center gap-3">
 *           <Skeleton className="size-8 rounded-full" />
 *           <div className="space-y-1">
 *             <Skeleton className="h-4 w-24" />
 *             <Skeleton className="h-3 w-16" />
 *           </div>
 *         </div>
 *       ),
 *     },
 *   },
 * ];
 * ```
 */
function DataTableSkeleton({ rows = 5, className }: DataTableSkeletonProps) {
  const table = useDataTable();
  const columns = table.getAllLeafColumns();

  return (
    <Table
      className={cn(
        "w-auto min-w-full table-auto",
        "[&_th:not(:last-child)]:border-border [&_th:not(:last-child)]:border-r",
        "[&_td:not(:last-child)]:border-border [&_td:not(:last-child)]:border-r",
        className
      )}
    >
      <DataTableHeader />
      <TableBody>
        {Array.from({ length: rows }, (_, rowIndex) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static list, items never reorder
          <TableRow key={`skeleton-row-${rowIndex + 1}`}>
            {columns.map((column, colIndex) => {
              const meta = column.columnDef.meta as
                | { skeleton?: ReactNode }
                | undefined;

              return (
                <TableCell key={column.id}>
                  {meta?.skeleton ?? (
                    <Skeleton
                      className={cn(
                        "h-4",
                        SKELETON_WIDTHS[
                          (rowIndex + colIndex) % SKELETON_WIDTHS.length
                        ]
                      )}
                    />
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export type { DataTableSkeletonProps };
export { DataTableSkeleton };
